import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { actionProveedoresGet } from "../../redux/actions/proveedores/proveedores";

import { Button, Card, Input, Space, Typography, Row, Col, Skeleton, ConfigProvider } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showHerramientasEffect } from "../../components/utils/antWaveEffect";
import "./PaquetesPage.css";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, #3d0011 0%, #5c0019 100%)";

export default function ProveedoresPage() {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.proveedores);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");

  const lastFetchKey = useRef("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchParams = useMemo(() => ({ search: searchDebounced || undefined }), [searchDebounced]);

  useEffect(() => {
    const key = JSON.stringify(fetchParams);
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    dispatch(actionProveedoresGet(fetchParams));
  }, [dispatch, fetchParams]);

  const counts = useMemo(() => ({
    total: items.length,
    conCredito: items.filter((p) => Boolean(p.da_credito)).length,
    sinCredito: items.filter((p) => !p.da_credito).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "con_credito") return items.filter((p) => Boolean(p.da_credito));
    if (activeFilter === "sin_credito") return items.filter((p) => !p.da_credito);
    return items;
  }, [items, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de proveedores", icon: <i className="fa-solid fa-truck-field" />, iconCls: "paq-stat-icon-sonido", count: counts.total },
    { key: "con_credito", label: "Con credito", icon: <i className="fa-solid fa-money-check-dollar" />, iconCls: "paq-stat-icon-decoracion", count: counts.conCredito },
    { key: "sin_credito", label: "Sin credito", icon: <i className="fa-solid fa-ban" />, iconCls: "paq-stat-icon-barra", count: counts.sinCredito },
  ];

  return (
    <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Proveedores</Title>
                <Text className="paq-subtitle">Catalogo de proveedores que surten mercancia a la tienda.</Text>
              </Space>

              <Row gutter={[16, 14]}>
                <Col xs={24}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre o clave del proveedor..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
              </Row>
            </div>

            <div className="paq-stats-row" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
              {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                <Card
                  key={key}
                  className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                  hoverable
                  onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                >
                  <Space align="center" size={10}>
                    <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                    <div>
                      <div className="paq-stat-value">{count}</div>
                      <div className="paq-stat-label">{label}</div>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Proveedores ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} proveedores encontrados</Text>
                </div>
                <div className="paq-toolbar-right">
                  <>
                    <Button
                      type="primary"
                      icon={<i className="fa-solid fa-plus" />}
                      onClick={() => navigate("/app/proveedores/crear")}
                      style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
                    >
                      Nuevo proveedor
                    </Button>
                  </>
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <div style={{ fontSize: 16, fontWeight: 500 }}>Sin proveedores que coincidan con los filtros</div>
                </div>
              )}

              <div className="paq-category-block">
                <div className="paq-grid">
                  {loading && Array.from({ length: 4 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                  {!loading && filteredItems.length > 0 && (
                    <>
                      {filteredItems.map((p) => (
                        <div key={p.id_proveedor} className="paq-card">
                          <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                            <i className="fa-solid fa-truck-field paq-card-banner-icon" />
                          </div>

                          <div className="paq-card-header">
                            <div className="paq-card-head">
                              <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>{p.nombre}</Paragraph>
                              {p.clave_proveedor && (
                                <span className="paq-tipo-badge paq-tipo-sonido">{p.clave_proveedor}</span>
                              )}
                            </div>
                          </div>

                          <div className="paq-card-body">
                            <div className="paq-contenidos-box">
                              <i className="fa-solid fa-boxes-stacked paq-info-icon" />
                              <div className="paq-info-text">
                                <span className="paq-info-label">Linea que surte</span>
                                <span className="paq-info-value">{p.linea_surte || "No especificada"}</span>
                              </div>
                            </div>
                            <div className="paq-contenidos-box">
                              <i className="fa-solid fa-phone paq-info-icon" />
                              <div className="paq-info-text">
                                <span className="paq-info-label">Telefono</span>
                                <span className="paq-info-value">{p.telefono || "No registrado"}</span>
                              </div>
                            </div>
                          </div>

                          <div className="paq-card-footer">
                            {Boolean(p.da_credito) && (
                              <div className="paq-bottom-strip is-green">
                                <span className="paq-strip-label">Da credito {p.plazo_dias_credito ? `(${p.plazo_dias_credito}d)` : ""}</span>
                              </div>
                            )}
                            <Button
                              className="paq-btn-details"
                              icon={<i className="fa-solid fa-circle-info" />}
                              onClick={() => navigate(`/app/proveedores/${p.id_proveedor}`)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </ConfigProvider>
  );
}
